import { useEffect, useRef, useState } from "react";
import { AddMeal } from "alerts";
import dateTime from "utils/helpers/dateTime";
import classNames from "classnames";
import { isEmpty, isPresent } from "utils/helpers/array";
import dayjs from "dayjs";
import AddressTimeModal from "components/AddressTimeModal";
import moment from "moment-timezone";

function MealCard({ type, meal, locationId, navigate }) {
  const showMealInfo =
    (!meal?.under_review &&
      type == "uncatering" &&
      !meal?.all_orders_cancelled) ||
    (type != "uncatering" && !meal?.all_orders_cancelled);

  const isPastDeadline = moment(meal?.cutoff, "YYYY-MM-DD hh:mm A").isBefore(
    moment()
  );

  return (
    <button
      onClick={() => {
        type == "grouped"
          ? navigate(
              `/group-order/admin/orders/${meal?.meal_series_id}/${locationId}`
            )
          : type == "odc" || type == "catering"
          ? navigate(
              `/meal-management/catering-order/${meal?.orders[0]?.slug}/${locationId}`
            )
          : !meal?.under_review &&
            navigate(`/home/uncatering-meal/${meal?.id}`);
      }}
      className="px-[8px] w-full"
    >
      <div
        className={classNames({
          "h-[11px] w-full": true,
          "bg-accent-gray": meal?.all_orders_cancelled,
          "bg-catering": type == "odc" && !meal?.all_orders_cancelled,
          "bg-uncatering": type == "uncatering" && !meal?.all_orders_cancelled,
          "bg-group": type == "grouped" && !meal?.all_orders_cancelled,
        })}
      />

      <div className="bg-light-gray w-full flex flex-col items-start text-left px-[8px] py-[12px] mb-[12px]">
        <small
          className={classNames({
            "capitalize font-inter-medium text-[12px]": true,
            "text-secondary": meal?.all_orders_cancelled,
            "text-catering": type == "odc" && !meal?.all_orders_cancelled,
            "text-uncatering":
              type == "uncatering" && !meal?.all_orders_cancelled,
            "text-group": type == "grouped" && !meal?.all_orders_cancelled,
          })}
        >
          {type == "odc"
            ? "catering"
            : type == "grouped"
            ? "Group order"
            : "Uncatering"}
        </small>
        <p className="text-dark-gray font-inter-semibold text-[16px]">
          {type !== "odc" &&
            (meal?.name?.length > 25
              ? `${meal?.name.slice(0, 25)}...`
              : meal?.name)}
        </p>

        {showMealInfo && (
          <>
            {type !== "odc" &&
              type !== "catering" &&
              (() => {
                const shouldShowDeadlineText =
                  (type === "grouped" &&
                    !isPastDeadline &&
                    meal?.orders?.length < 1) ||
                  type !== "grouped";

                const shouldShowDeadlineDate =
                  type === "grouped" &&
                  !isPastDeadline &&
                  meal?.orders?.length < 1;

                if (!shouldShowDeadlineText && !shouldShowDeadlineDate)
                  return null;

                return (
                  <span className="text-dark-gray text-[12px] font-inter-medium">
                    {shouldShowDeadlineText &&
                      (type === "grouped"
                        ? "Deadline to place order:"
                        : "Deadline to order")}

                    {shouldShowDeadlineDate && (
                      <span className="font-inter-semibold ml-1">
                        {dateTime.formatDate(
                          dateTime.onlyDate(meal?.cutoff),
                          "ddd, MMMM Do, yyyy"
                        )}
                        {" - "}
                        {dateTime.formatTimeWithFormat(
                          meal?.cutoff,
                          "h:mm A",
                          meal?.tzinfo
                        )}
                      </span>
                    )}

                    {type !== "grouped" && (
                      <span className="font-inter-semibold ml-1">
                        {dateTime.formatTimeWithFormat(
                          meal?.cutoff,
                          "h:mm A",
                          meal?.tzinfo
                        )}
                      </span>
                    )}
                  </span>
                );
              })()}

            <span className="text-dark-gray text-[12px] font-inter-medium">
              Delivery time:
              <span className="font-inter-semibold ml-1">
                {dateTime.to12hourWithFormat(
                  dateTime?.onlyTime(meal?.target_time),
                  meal?.tzinfo,
                  "h:mm A"
                )}
              </span>
            </span>

            {type == "uncatering" && (
              <span className="text-dark-gray text-[12px] font-inter-medium">
                Expected orders:
                <span className="font-inter-semibold ml-1">
                  {meal.expected_bags}
                </span>
              </span>
            )}
          </>
        )}

        {meal?.under_review && type == "uncatering" && (
          <small className="font-inter-medium text-[12px] text-[#E39F55] pb-[4px]">
            Under review
          </small>
        )}

        {isPresent(meal?.shipments) && (
          <div className="p-[8px] bg-white w-full flex flex-col rounded-lg">
            {meal?.shipments?.map((shipment) => (
              <small className="font-inter-medium capitalize text-[12px] text-dark-gray pb-[4px]">
                {meal?.service == "uncatering"
                  ? shipment?.menu_display_name_uncatering
                  : shipment?.menu_display_name_odc}
              </small>
            ))}
          </div>
        )}

        {isEmpty(meal?.shipments) &&
          !meal?.published &&
          type == "uncatering" &&
          !meal?.under_review && (
            <div className="p-[8px] bg-white flex flex-col rounded-lg">
              <small className="font-inter-medium text-[12px] text-[#566985]">
                Restaurants to be picked soon...
              </small>
            </div>
          )}
      </div>
    </button>
  );
}

export default function WeeklyOrders({
  data,
  locationId,
  navigate,
  defaultLocation,
}) {
  const container = useRef(null);
  const containerNav = useRef(null);
  const containerOffset = useRef(null);
  const [addMeal, setAddMeal] = useState(false);
  const [addressTimeModal, setAddressTimeModal] = useState(false);
  const [orderType, setOrderType] = useState("Catering");

  useEffect(() => {
    // Set the container scroll position based on the current time.
    const currentMinute = new Date().getHours() * 60;
    container.current.scrollTop =
      ((container.current.scrollHeight -
        containerNav.current?.offsetHeight -
        containerOffset.current?.offsetHeight) *
        currentMinute) /
      1440;
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div
        ref={container}
        className="isolate flex flex-auto flex-col overflow-auto bg-white"
      >
        <div
          style={{ width: "165%" }}
          className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full"
        >
          <div
            ref={containerNav}
            className="sticky top-0 bg-white flex-col items-center justify-center content-center"
          >
            <div className="grid grid-cols-7 text-sm leading-6 text-gray-500">
              {data?.map((day) => (
                <div className="flex flex-col w-full items-center pb-3 pt-2 border-r pb-[12px]">
                  <span
                    className={classNames({
                      "mt-1 flex items-center justify-center font-inter-regular text-[14px]": true,
                      "text-catering":
                        dayjs(day?.date).format("DD") == dayjs().format("DD"),
                      "text-dark-gray":
                        dayjs(day?.date).format("DD") != dayjs().format("DD"),
                    })}
                  >
                    {dayjs(day?.date).format("DD")}
                  </span>

                  <span
                    className={classNames({
                      "flex items-center justify-center font-inter-regular text-[14px]": true,
                      "text-catering":
                        dayjs(day?.date).format("DD") == dayjs().format("DD"),
                      "text-dark-gray":
                        dayjs(day?.date).format("DD") != dayjs().format("DD"),
                    })}
                  >
                    {dayjs(day?.date).format("dddd")}
                  </span>

                  <button
                    onClick={() => setAddMeal(true)}
                    type="button"
                    className="flex flex-col items-center mb-[12px] justify-center bg-sidebar-gray w-[144px] h-[38px] rounded-sm hover:bg-gray-200 mx-auto"
                  >
                    <span className="font-inter-regular text-dark-gray">
                      + Add meal
                    </span>
                  </button>

                  {day.meal?.map((meal) => (
                    <MealCard
                      type={meal?.service ?? "uncatering"}
                      meal={meal}
                      locationId={locationId}
                      navigate={navigate}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <AddMeal
        visible={addMeal}
        setModal={(value) => setAddMeal(value)}
        handleAddressTimeModal={setAddressTimeModal}
        setMealType={(value) => setOrderType(value)}
        deliveryInfo={true}
      />

      <AddressTimeModal
        visibleModal={addressTimeModal}
        showModal={setAddressTimeModal}
        orderType={orderType}
      />
    </div>
  );
}
